import React, { useState } from 'react';
import { Button, Input, Upload, message, Spin } from 'antd';
import { SendOutlined, CloseCircleOutlined, PictureOutlined, FileOutlined, CloseOutlined } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import { FaPaperclip } from 'react-icons/fa';

const { TextArea } = Input;

const MessageInput = ({
    messageInput,
    setMessageInput,
    onSendMessage,
    isSending,
    isClosed
}) => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [photoId, setPhotoId] = useState(null);
    const [docId, setDocId] = useState(null);
    const [docName, setDocName] = useState('');

    const handleImageUpload = async ({ file }) => {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await drEasyApi.post("/api/Storage/UploadImage", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const imageId = res.data.id;
            setPhotoId(imageId);
            message.success("Image uploaded successfully");
        } catch (err) {
            message.error("Failed to upload image");
            console.error(err);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDocUpload = async ({ file }) => {
        setUploadingDoc(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await drEasyApi.post("/api/Storage/UploadPDF", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const documentId = res.data.id;
            setDocId(documentId);
            setDocName(file.name);
            message.success("Document uploaded successfully");
        } catch (err) {
            message.error("Failed to upload document");
            console.error(err);
        } finally {
            setUploadingDoc(false);
        }
    };

    const handleSend = () => {
        // Pass the photo and doc IDs along with the message
        onSendMessage(messageInput, photoId, docId);

        // Reset all fields
        setMessageInput('');
        setPhotoId(null);
        setDocId(null);
        setDocName('');
    };

    const removeImage = () => {
        setPhotoId(null);
    };

    const removeDoc = () => {
        setDocId(null);
        setDocName('');
    };

    if (isClosed) {
        return (
            <div className="shrink-0 w-full p-3 sm:p-4 bg-white border-t border-gray-200 shadow-xl">
                <div className="text-center py-3 bg-gray-50 rounded-lg">
                    <CloseCircleOutlined className="text-gray-400 text-lg mb-1" />
                    <p className="text-sm text-gray-500 m-0">This conversation is closed</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="shrink-0 w-full p-2 sm:p-4 bg-white border-t border-gray-200 shadow-md"
            style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
            {/* Attachments Preview */}
            {(photoId || docId) && (
                <div className="mb-2 sm:mb-3 space-y-2">
                    {photoId && (
                        <div className="relative inline-block">
                            <img
                                src={getImg(photoId)}
                                alt="Preview"
                                className="rounded max-h-24 sm:max-h-32 max-w-full border border-gray-200"
                            />
                            <Button
                                icon={<CloseOutlined />}
                                size="small"
                                danger
                                className="absolute top-1 right-1"
                                onClick={removeImage}
                            />
                        </div>
                    )}

                    {docId && (
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-2 max-w-full">
                            <FileOutlined className="text-blue-600 shrink-0" />
                            <span className="text-sm text-gray-700 truncate min-w-0 flex-1">{docName}</span>
                            <Button
                                icon={<CloseOutlined />}
                                size="small"
                                danger
                                type="text"
                                className="shrink-0"
                                onClick={removeDoc}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-end gap-1.5 sm:gap-2">
                {/* Image Upload */}
                <Upload
                    showUploadList={false}
                    customRequest={handleImageUpload}
                    accept=".jpg,.jpeg,.png"
                    disabled={uploadingImage || uploadingDoc}
                >
                    <Button
                        icon={uploadingImage ? <Spin size="small" /> : <PictureOutlined className='text-primary' />}
                        disabled={uploadingImage || uploadingDoc}
                        title="Upload Image"
                    />
                </Upload>

                {/* Document Upload */}
                <Upload
                    showUploadList={false}
                    customRequest={handleDocUpload}
                    accept=".pdf"
                    disabled={uploadingImage || uploadingDoc}
                >
                    <Button
                        icon={uploadingDoc ? <Spin size="small" /> : <FaPaperclip className='text-primary' />}
                        disabled={uploadingImage || uploadingDoc}
                        title="Upload PDF"
                    />
                </Upload>

                {/* Message Input (16px on mobile so iOS doesn't auto-zoom) */}
                <TextArea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Type your message..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onPressEnter={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    className="flex-1 min-w-0 !text-base sm:!text-sm"
                    disabled={uploadingImage || uploadingDoc}
                />

                {/* Send Button */}
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    loading={isSending}
                    disabled={(!messageInput.trim() && !photoId && !docId) || uploadingImage || uploadingDoc}
                    className="bg-[#298d9f] hover:bg-[#236d7c] shrink-0"
                    size="middle"
                />
            </div>
        </div>
    );
};

export default MessageInput;